import { useState } from "react";
import { ShieldAlert } from "lucide-react";
import { deletePrimeCompany, type DeletePrimeCompanyResult, type PrimeCompanyDetail } from "../api/CrmApi";
import { apiErrorMessage } from "../apiError";
import { formatDate } from "./crmUi";
import ConfirmModal from "shared/ConfirmModal";
import Modal from "shared/Modal";
import Button from "shared/Button";
import Alert from "shared/Alert";

interface Props {
  company: PrimeCompanyDetail;
  onClose: () => void;
  onDeleted: (result: DeletePrimeCompanyResult) => void;
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const STATUS_LABELS: Record<string, string> = { do_not_contact: "Do Not Contact", bounced: "Bounced", not_a_fit: "Not a fit", inactive: "Inactive" };

/**
 * Delete a company. When the no-repeat-contact rules still rely on its records (a campaign that still locks it, a contact
 * or the company itself marked Do Not Contact / Bounced) the server refuses the delete, so this shows why and offers no
 * delete button; otherwise it is a plain confirmation that states exactly what goes with the company.
 */
export default function DeleteCompanyModal({ company, onClose, onDeleted }: Props) {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");

  const blockers = company.delete_blockers;
  const impact = company.delete_impact;
  const contacts = company.prime_contacts ?? [];

  async function confirmDelete() {
    setDeleting(true);
    setError("");
    try {
      onDeleted(await deletePrimeCompany(company.id));
    } catch (err: any) {
      setError(apiErrorMessage(err, "Could not delete the company. Please try again."));
      setDeleting(false);
    }
  }

  if (blockers?.blocked) {
    return (
      <Modal
        size="md"
        title={`${company.name || "This company"} cannot be deleted yet`}
        description="The no-repeat-contact rules still rely on its records"
        icon={<ShieldAlert />}
        onClick={onClose}
        closeButtonCLick={onClose}
        footer={<Button variant="outline" size="sm" onClick={onClose}>Close</Button>}
      >
        <ul className="space-y-3 text-sm text-foreground" data-testid="delete-blocked">
          {blockers.campaigns.map((c) => (
            <li key={c.opportunity_id}>
              <span className="font-medium">{c.title}</span> — a campaign still locks this company until{" "}
              <span className="font-medium">{formatDate(c.locked_until, true)}</span> (the campaign's last touch plus the 7-day
              cooldown). Deleting it now would let another campaign contact it again.
            </li>
          ))}
          {blockers.flagged_contacts.map((f) => (
            <li key={f.email}>
              <span className="font-medium">{f.email}</span> is marked {STATUS_LABELS[f.status] ?? f.status}. Remove the mark on
              the contact's page first; it would otherwise be deleted with the contact.
            </li>
          ))}
          {blockers.company_status && (
            <li>
              The company itself is marked <span className="font-medium">{STATUS_LABELS[blockers.company_status] ?? blockers.company_status}</span>.
              {blockers.company_status === "not_a_fit" || blockers.company_status === "inactive"
                ? "Re-open it on the company page first; the mark (and the reason) would otherwise be deleted with it, and a later sheet would bring the company back as new."
                : "Set its status back to active (Edit) first; the mark would otherwise be deleted with it."}
            </li>
          )}
        </ul>
      </Modal>
    );
  }

  return (
    <ConfirmModal
      title={`Delete ${company.name || "this company"}?`}
      variant="danger"
      confirmLabel="Delete company"
      confirming={deleting}
      onCancel={onClose}
      onConfirm={confirmDelete}
      message={
        <div className="space-y-3" data-testid="delete-company-message">
          <p>
            {impact ? (
              <>
                This permanently deletes the company, its {plural(impact.prime_contacts, "contact person", "contact people")},
                {" "}{plural(impact.rfp_contact_rows, "RFP contact record")} and {plural(impact.rfp_roles, "RFP role")}{impact.touches ? <>, {plural(impact.touches, "LinkedIn touch", "LinkedIn touches")} with their history</> : null}{impact.campaign_companies ? <> and {plural(impact.campaign_companies, "campaign record")}</> : null}. {impact.conversations ? <>It also deletes {plural(impact.conversations, "conversation")} with {plural(impact.conversation_messages ?? 0, "message")}. </> : null}It cannot be undone.
              </>
            ) : (
              <>
                This permanently deletes the company, its {plural(contacts.length, "contact person", "contact people")} and
                its links to {plural(company.opportunities?.length ?? 0, "RFP")}. It cannot be undone.
              </>
            )}
          </p>
          {error && <Alert tone="danger" role="alert">{error}</Alert>}
        </div>
      }
    />
  );
}
